import React from 'react'
import { cn } from '../lib/utils'
import { colors } from '../tokens/colors'

// Enums do botão
export enum ButtonStyle {
  FILLED = 'filled',
  BORDERED = 'bordered', 
  CLEAR = 'clear',
  FILLED_BORDERED = 'filled-bordered'
}

type ButtonSize = 'xs' | 'sm' | 'md'

// Interface para aparência (configuração principal)
interface ButtonAppearance {
  backgroundColor?: string
  textColor?: string
  borderColor?: string
}

// Interface para estados do botão
interface ButtonStateAppearance {
  enabled: ButtonAppearance
  hover: ButtonAppearance
  loading: ButtonAppearance
  disabled: ButtonAppearance
}

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  // Modo preset: usar variant (ignora buttonStyle e appearance)
  variant?: ButtonVariant
  
  // Modo manual: usar quando variant não definido
  buttonStyle?: ButtonStyle
  size?: ButtonSize
  appearance?: ButtonAppearance
  
  // Estados
  loading?: boolean
  
  children: React.ReactNode
}

// Enum de variantes para presets
export enum ButtonVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  NEGATIVE = 'negative',
  POSITIVE = 'positive',
  WARNING = 'warning'
}


// Função para gerar estados para um estilo
const createButtonStates = (base: ButtonAppearance): ButtonStateAppearance => ({
  enabled: base,
  hover: {
    ...base,
    backgroundColor: base.backgroundColor === 'transparent' 
      ? colors.interactive.hover 
      : base.backgroundColor,
  },
  loading: {
    backgroundColor: colors.palette.gray[100],
    textColor: colors.palette.gray[400],
    borderColor: colors.palette.gray[200],
  },
  disabled: {
    backgroundColor: colors.palette.gray[100],
    textColor: colors.palette.gray[400],
    borderColor: colors.palette.gray[200],
  },
})

// Presets de variantes com estados
const variantPresets: Record<ButtonVariant, Record<ButtonStyle, ButtonStateAppearance>> = {
  [ButtonVariant.PRIMARY]: {
    [ButtonStyle.FILLED]: createButtonStates({
      backgroundColor: colors.brand.primary,
      textColor: colors.background.white,
      borderColor: colors.brand.primary,
    }),
    [ButtonStyle.BORDERED]: createButtonStates({
      backgroundColor: 'transparent',
      textColor: colors.brand.primary,
      borderColor: colors.brand.primary,
    }),
    [ButtonStyle.CLEAR]: createButtonStates({
      backgroundColor: 'transparent',
      textColor: colors.brand.primary,
      borderColor: 'transparent',
    }),
    [ButtonStyle.FILLED_BORDERED]: createButtonStates({
      backgroundColor: colors.brand.primary,
      textColor: colors.background.white,
      borderColor: colors.brand.secondary,
    }),
  },
  [ButtonVariant.SECONDARY]: {
    [ButtonStyle.FILLED]: createButtonStates({
      backgroundColor: colors.brand.primary,
      textColor: colors.background.white,
      borderColor: colors.brand.primary,
    }),
    [ButtonStyle.BORDERED]: createButtonStates({
      backgroundColor: 'transparent',
      textColor: colors.brand.primary,
      borderColor: colors.brand.primary,
    }),
    [ButtonStyle.CLEAR]: createButtonStates({
      backgroundColor: 'transparent',
      textColor: colors.brand.primary,
      borderColor: 'transparent',
    }),
    [ButtonStyle.FILLED_BORDERED]: createButtonStates({
      backgroundColor: colors.brand.primary,
      textColor: colors.background.white,
      borderColor: colors.brand.secondary,
    }),
  },
  [ButtonVariant.NEGATIVE]: {
    [ButtonStyle.FILLED]: createButtonStates({
      backgroundColor: colors.status.negative.background,
      textColor: colors.status.negative.text,
      borderColor: colors.status.negative.border,
    }),
    [ButtonStyle.BORDERED]: createButtonStates({
      backgroundColor: 'transparent',
      textColor: colors.status.negative.text,
      borderColor: colors.status.negative.text,
    }),
    [ButtonStyle.CLEAR]: createButtonStates({
      backgroundColor: 'transparent',
      textColor: colors.status.negative.text,
      borderColor: 'transparent',
    }),
    [ButtonStyle.FILLED_BORDERED]: createButtonStates({
      backgroundColor: colors.status.negative.background,
      textColor: colors.status.negative.text,
      borderColor: colors.status.negative.text,
    }),
  },
  [ButtonVariant.POSITIVE]: {
    [ButtonStyle.FILLED]: createButtonStates({
      backgroundColor: colors.status.positive.background,
      textColor: colors.status.positive.text,
      borderColor: colors.status.positive.border,
    }),
    [ButtonStyle.BORDERED]: createButtonStates({
      backgroundColor: 'transparent',
      textColor: colors.status.positive.text,
      borderColor: colors.status.positive.text,
    }),
    [ButtonStyle.CLEAR]: createButtonStates({
      backgroundColor: 'transparent',
      textColor: colors.status.positive.text,
      borderColor: 'transparent',
    }),
    [ButtonStyle.FILLED_BORDERED]: createButtonStates({
      backgroundColor: colors.status.positive.background,
      textColor: colors.status.positive.text,
      borderColor: colors.status.positive.text,
    }),
  },
  [ButtonVariant.WARNING]: {
    [ButtonStyle.FILLED]: createButtonStates({
      backgroundColor: colors.status.warning.background,
      textColor: colors.status.warning.text,
      borderColor: colors.status.warning.border,
    }),
    [ButtonStyle.BORDERED]: createButtonStates({
      backgroundColor: 'transparent',
      textColor: colors.status.warning.text,
      borderColor: colors.status.warning.text,
    }),
    [ButtonStyle.CLEAR]: createButtonStates({
      backgroundColor: 'transparent',
      textColor: colors.status.warning.text,
      borderColor: 'transparent',
    }),
    [ButtonStyle.FILLED_BORDERED]: createButtonStates({
      backgroundColor: colors.status.warning.background,
      textColor: colors.status.warning.text,
      borderColor: colors.status.warning.text,
    }),
  },
}

// Função para obter preset de variante com estado
export function getButtonVariantPreset(
  variant: ButtonVariant, 
  style: ButtonStyle = ButtonStyle.FILLED,
  state: keyof ButtonStateAppearance = 'enabled'
): ButtonAppearance {
  return variantPresets[variant][style][state]
}

// Função para criar props de botão com preset
export function createButtonProps(
  variant: ButtonVariant, 
  style: ButtonStyle = ButtonStyle.FILLED, 
  overrides?: Partial<ButtonAppearance>
): Pick<ButtonProps, 'buttonStyle' | 'appearance'> {
  const preset = getButtonVariantPreset(variant, style, 'enabled')
  return {
    buttonStyle: style,
    appearance: {
      ...preset,
      ...overrides
    }
  }
}

export function Button({ 
  variant,
  buttonStyle = ButtonStyle.FILLED, 
  size = 'md', 
  appearance,
  loading = false,
  disabled = false,
  className, 
  children, 
  ...props 
}: ButtonProps) {
  const baseClasses = 'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
  
  const sizes = {
    xs: 'p-1.5 text-sm',
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2.5 text-sm',
  }

  // Determinar estado atual
  const currentState = disabled ? 'disabled' : loading ? 'loading' : 'enabled'

  // Determinar aparência final
  let finalAppearance: ButtonAppearance
  let effectiveButtonStyle: ButtonStyle

  if (variant) {
    // Se tem variant, usar o preset dele (sempre FILLED)
    effectiveButtonStyle = ButtonStyle.FILLED
    finalAppearance = variantPresets[variant][effectiveButtonStyle][currentState]
  } else {
    // Sem variant, usar configuração manual
    effectiveButtonStyle = buttonStyle || ButtonStyle.FILLED
    
    if (disabled || loading) {
      // Estados especiais
      finalAppearance = {
        backgroundColor: colors.palette.gray[100],
        textColor: colors.palette.gray[400],
        borderColor: colors.palette.gray[200],
      }
    } else {
      // Usar appearance customizada ou defaults
      finalAppearance = {
        backgroundColor: appearance?.backgroundColor || (effectiveButtonStyle === ButtonStyle.FILLED ? colors.palette.gray[600] : 'transparent'),
        textColor: appearance?.textColor || colors.text.primary,
        borderColor: appearance?.borderColor || (effectiveButtonStyle === ButtonStyle.BORDERED ? colors.border.primary : 'transparent'),
      }
    }
  }

  // Criar classes dinâmicas baseadas na configuração
  const buttonClasses = cn(
    baseClasses,
    sizes[size],
    // Border style
    (effectiveButtonStyle === ButtonStyle.BORDERED || effectiveButtonStyle === ButtonStyle.FILLED_BORDERED) ? 'border-2' : 'border-0',
    // Estados
    disabled && 'cursor-not-allowed',
    loading && 'cursor-wait',
    // Hover apenas se não disabled/loading
    !disabled && !loading && 'hover:scale-[1.02]',
    className
  )

  // Estilos inline para cores
  const inlineStyles: React.CSSProperties = {
    backgroundColor: finalAppearance.backgroundColor,
    color: finalAppearance.textColor,
    borderColor: finalAppearance.borderColor,
  }
  
  return (
    <button
      className={buttonClasses}
      style={inlineStyles}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          {children}
        </div>
      ) : (
        children
      )}
    </button>
  )
}